import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useRBAC } from '../../contexts/RBACContext';

const UserProfileDropdown = ({ currentUser: propUser }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { rbacRoles, rbacLoading } = useRBAC();

  // Derive the live role name from RBAC roles
  const rbacRoleName = rbacRoles?.length > 0
    ? rbacRoles?.map(r => r?.role_name)?.filter(Boolean)?.join(', ')
    : null;

  const currentUser = propUser || {
    name: user?.email?.split('@')?.[0] || 'User',
    role: 'Member',
    email: user?.email || ''
  };

  // Always use the live RBAC role name if available, otherwise fall back to prop role
  const displayRole = rbacLoading ? (currentUser?.role || 'Loading...') : (rbacRoleName || currentUser?.role || 'Member');

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event?.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleToggle = () => setIsOpen(!isOpen);
  const handleProfileClick = () => {
    setIsOpen(false);
    navigate('/account-management');
  };
  const handleSettingsClick = () => {
    setIsOpen(false);
    navigate('/account-management');
  };

  const handleLogoutClick = async () => {
    setIsOpen(false);
    await signOut();
    navigate('/sign-in');
  };

  const getInitials = (name) => {
    return name?.split(' ')?.map(word => word?.[0])?.join('')?.toUpperCase()?.slice(0, 2);
  };

  const getRoleBadgeColor = (role) => {
    if (!role) return 'bg-muted text-muted-foreground';
    const roleLower = role?.toLowerCase();
    if (roleLower?.includes('super_admin') || roleLower?.includes('super admin')) return 'bg-error/10 text-error';
    if (roleLower?.includes('admin')) return 'bg-error/10 text-error';
    if (roleLower?.includes('finance')) return 'bg-warning/10 text-warning';
    if (roleLower?.includes('sales')) return 'bg-primary/10 text-primary';
    if (roleLower?.includes('project')) return 'bg-accent/10 text-accent';
    if (roleLower?.includes('approver')) return 'bg-success/10 text-success';
    if (roleLower?.includes('auditor')) return 'bg-secondary/10 text-secondary';
    if (roleLower?.includes('hr')) return 'bg-accent/10 text-accent';
    if (roleLower?.includes('employee')) return 'bg-muted text-muted-foreground';
    const roleColors = {
      'Admin': 'bg-error/10 text-error',
      'Estimator': 'bg-primary/10 text-primary',
      'BDM': 'bg-accent/10 text-accent',
      'Procurement': 'bg-success/10 text-success',
      'Finance': 'bg-warning/10 text-warning',
      'Management': 'bg-secondary/10 text-secondary'
    };
    return roleColors?.[role] || 'bg-muted text-muted-foreground';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggle}
        className="flex items-center gap-3 h-12 px-4 rounded-md bg-card border border-border hover:bg-muted transition-smooth focus-ring"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary text-primary-foreground text-sm font-medium">
          {getInitials(currentUser?.name)}
        </div>
        <div className="hidden md:block text-left">
          <div className="text-sm font-medium text-foreground">{currentUser?.name}</div>
          <div className="text-xs text-muted-foreground">{displayRole}</div>
        </div>
        <Icon
          name="ChevronDown"
          size={16}
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-72 bg-popover border border-border rounded-lg shadow-xl z-dropdown">
          <div className="p-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-12 h-12 rounded-full bg-primary text-primary-foreground text-lg font-medium">
                {getInitials(currentUser?.name)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-popover-foreground truncate">
                  {currentUser?.name}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {currentUser?.email}
                </div>
                {currentUser?.jobTitle && (
                  <div className="text-xs text-muted-foreground truncate mt-0.5">
                    {currentUser?.jobTitle}
                  </div>
                )}
                <div className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium mt-1 ${getRoleBadgeColor(displayRole)}`}>
                  {displayRole}
                </div>
              </div>
            </div>
          </div>

          <div className="p-2">
            <button
              onClick={handleProfileClick}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-popover-foreground hover:bg-muted transition-smooth"
            >
              <Icon name="User" size={18} />
              <span>View Profile</span>
            </button>
            <button
              onClick={handleSettingsClick}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-popover-foreground hover:bg-muted transition-smooth"
            >
              <Icon name="Settings" size={18} />
              <span>Settings</span>
            </button>
          </div>

          <div className="p-2 border-t border-border">
            <button
              onClick={handleLogoutClick}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-error hover:bg-error/10 transition-smooth"
            >
              <Icon name="LogOut" size={18} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfileDropdown;